// Auto-DM Outreach — feature-owned types, defaults and endpoint docs.
// "Personalized DMs to the exact people who should be buying, sent at scale while you sleep."
//
// A campaign = one audience (Instagram source via the core harvester, CRM contacts, or a pasted
// username list) → filters → a message sequence (opener + up to 3 follow-ups, spintax + [vars],
// optional AI personalization) → optional "Follow + DM" warm-up (view story / like posts / follow,
// then DM on a timing rule). Replies stop the sequence (contacts.last_inbound_at > last touch).
//
// Import as `@gf/shared/features/outreach`.

import type { AudienceSource } from "../api";
import type { IgErrorKind, IgFriendship } from "../ig";
import type { ActionKind, ID, IgUserProfile, Millis, Page } from "../types";

// ── Campaign model ────────────────────────────────────────────────────────

export type OutreachCampaignStatus = "draft" | "active" | "paused" | "completed";

/** Attribution is recorded evidence, not proof of why a person followed. */
export type FollowerSourceFilter =
  | { kind: "all" | "organic" | "follow_for_follow" | "comments" }
  | { kind: "campaign"; feature?: "outreach" | "engage"; campaignId?: ID };

export const FOLLOWER_SOURCE_LABELS = {
  all: "All sources", organic: "Organic / unattributed", follow_for_follow: "Follow-for-follow",
  campaign: "Campaign-attributed", comments: "Comment replies",
} as const;

/** Who the campaign talks to. Exactly one per campaign. */
export type OutreachAudience =
  /** Harvested from Instagram by the core harvester (followers of X, hashtag, my new followers…). */
  | { kind: "instagram"; source: AudienceSource; /** Only my_followers / my_new_followers. */ followerSource?: FollowerSourceFilter }
  /** Existing CRM contacts of the campaign's IG account; empty lists = no constraint (AND between the two, OR within each). */
  | { kind: "crm"; stageIds: ID[]; tagIds: ID[] }
  /** Pasted usernames; resolved to Instagram ids by the extension at send time. */
  | { kind: "usernames"; usernames: string[] };

export interface OutreachFilters {
  /** Empty disables AI fit screening. Checked before the first campaign action. */
  aiFitCriteria?: string;
  /** Skip people any campaign (or you, by hand) already DMed — contact.lastOutboundAt is set. */
  skipContacted: boolean;
  /** Skip people you already have a DM conversation with (thread known or they messaged you). */
  skipExistingThread: boolean;
  /** Check the relationship before the first campaign action. */
  skipFollowers: boolean;
  skipPrivate: boolean;
  skipVerified: boolean;
  skipBusiness: boolean;
  minFollowers: number | null;
  maxFollowers: number | null;
  /** Bio must contain at least one of these (case-insensitive). Empty = no constraint. */
  requireBioKeywords: string[];
  /** Bio must contain none of these. */
  excludeBioKeywords: string[];
  /** Skip accounts still on Instagram's default avatar. */
  requireProfilePic: boolean;
}

/** steps[0] is the opener; steps[1..3] are follow-ups sent `afterDays` after the previous message. */
export interface OutreachStep {
  /** Ignored for the opener. 1–30 for follow-ups. */
  afterDays: number;
  /** Rotated at random. Spintax {a|b} + [first_name|there] variables (see TEMPLATE_HELP). */
  variants: string[];
  /** Rewrite the picked variant for each person with AI (falls back to the template). */
  aiPersonalize: boolean;
  /**
   * With aiPersonalize: take a Profile Snapshot of each person first (their latest posts, looked at by the
   * Image analysis model) so the AI can bring up something real from their posts. The DM waits up to
   * SNAPSHOT_LIMITS.outreachWaitMs for it, then goes out without (features/snapshots.ts).
   */
  lookAtProfile?: boolean;
}

export interface OutreachAiConfig {
  /** What this campaign is for, e.g. "book discovery calls with new moms in Austin". */
  goal: string;
  /** What you're offering, e.g. "free 20-min form check". */
  offer: string;
}

export type FollowMessageTiming = "after_follow" | "after_delay" | "after_follow_back";

/** "Follow + DM" campaigns: warm up, follow, then DM on a timing rule. */
export type CampaignAction = "follow" | "engage" | "message";
export interface OutreachFollowConfig {
  /** Ordered actions for unified campaigns. Absent on legacy campaigns. */
  sequence?: CampaignAction[];
  enabled: boolean;
  warmup: { likePosts: 0 | 1 | 2; viewStories: boolean };
  /**
   * after_follow      — DM 30–180 s after following (never instant).
   * after_delay       — DM a random delayMinutes[0]–[1] minutes after following.
   * after_follow_back — wait until they follow back (checked every ~30 min), up to followBackWaitDays.
   */
  messageTiming: FollowMessageTiming;
  delayMinutes: [number, number];
  followBackWaitDays: number;
  /** after_follow_back only: what happens when the wait runs out. */
  noFollowBack: "skip" | "message_anyway";
  /**
   * Optional cleanup: unfollow accounts THIS campaign followed when the sequence ends without a
   * reply and they don't follow back, this many days after the last touch. null = never.
   */
  unfollowIfNoReplyDays: number | null;
  /** After the sequence ends, clean up non-followers this many days after following. null = off. */
  unfollowIfNoFollowBackDays: number | null;
  /**
   * After the sequence ends, also unfollow people who DID follow you back, this many days after they
   * followed back. Most keep following you, so your following count (and followers ÷ following) stays
   * lean. Only accounts THIS campaign followed; anyone who ever messaged you is kept. null = off.
   */
  unfollowFollowBacksAfterDays: number | null;
}

/** Why a campaign clean-up unfollow happened (shown in the activity log and the worker window). */
export type OutreachCleanupReason = "followed_back" | "no_follow_back" | "no_reply";

export interface OutreachCampaignSettings {
  /** Review actual recipients and frozen text before sending. New campaigns default on. */
  requireApproval?: boolean;
  /** Move contacts to a stage when the opener is sent. */
  moveOnSend: boolean;
  /** null = the "Contacted" stage (by name), else your first open stage. Only ever moves contacts forward. */
  onSendStageId: ID | null;
  /** Move contacts here when they reply. null = don't move. */
  onReplyStageId: ID | null;
}

export interface OutreachHarvestState {
  cursor: string | null;
  exhausted: boolean;
  lastAt: Millis | null;
  /** Last harvest error shown on the campaign (deleted post, private account…). */
  error: string | null;
  /** Users added from the source so far. */
  added: number;
  /** my_new_followers (own mode): the first harvest only records who already follows you. */
  seeded: boolean;
  /**
   * my_new_followers (tracker mode): how far we've read the Follower Tracker's `gained` events
   * (keyset on detected_at, id). null = nothing read yet (starts at launch).
   */
  tracker: { cursorAt: Millis; cursorId: string; lastAt: Millis | null } | null;
}

/**
 * Where a "Welcome new followers" campaign gets its people:
 *   tracker — the Follower Tracker is tracking this IG account (baseline done + scheduled scans on):
 *             every new follower its daily scans detect after launch is welcomed. No extra reads.
 *   own     — the tracker is off/not set up for this account: outreach checks the newest page of
 *             your followers itself about every 20 minutes.
 */
export interface OutreachWelcomeStatus {
  mode: "tracker" | "own";
  /** The tracker has a baseline for this account. */
  trackerTracking: boolean;
  /** Scheduled tracker scans are on (settings.followers enabled + dailyScan). */
  trackerScheduled: boolean;
  /** Last finished tracker list scan / next scheduled one (tracker mode). */
  lastScanAt: Millis | null;
  nextScanAt: Millis | null;
}

export interface OutreachCampaign {
  id: ID;
  igAccountId: ID;
  name: string;
  status: OutreachCampaignStatus;
  audience: OutreachAudience;
  filters: OutreachFilters;
  steps: OutreachStep[];
  ai: OutreachAiConfig;
  follow: OutreachFollowConfig;
  /** Max DMs per rolling 24h (opener + follow-ups); for Follow + DM also max new people started per 24h. */
  dailyCap: number;
  settings: OutreachCampaignSettings;
  harvest: OutreachHarvestState;
  launchedAt: Millis | null;
  createdAt: Millis;
  updatedAt: Millis;
}

export interface OutreachCampaignInput {
  igAccountId: ID;
  name: string;
  audience?: OutreachAudience;
  filters?: Partial<OutreachFilters>;
  steps?: OutreachStep[];
  ai?: Partial<OutreachAiConfig>;
  follow?: Partial<OutreachFollowConfig>;
  dailyCap?: number;
  settings?: Partial<OutreachCampaignSettings>;
}

export type OutreachCampaignPatch = Partial<Omit<OutreachCampaignInput, "igAccountId">> & { igAccountId?: ID };

// ── Targets (one row per person per campaign) ─────────────────────────────

export type OutreachTargetStatus =
  | "queued" // waiting for its next action (phase says which)
  | "sending" // an action is leased to the extension right now (10-min lease)
  | "awaiting_follow_back" // Follow + DM: followed, waiting for them to follow back
  | "sent" // opener sent, follow-ups scheduled
  | "replied"
  | "skipped"
  | "failed"
  | "completed"; // every message sent, no reply (a late reply still flips it to replied)

/**
 * The next thing that happens to a target:
 * warmup (story view / like) → follow → await_follow_back → dm (opener) → followups → unfollow (cleanup) → done.
 * DM-only campaigns start at `dm`.
 */
export type OutreachPhase = "warmup" | "follow" | "await_follow_back" | "dm" | "followups" | "unfollow" | "done";

export type OutreachAction = "story_view" | "like" | "follow" | "dm" | "unfollow";

/**
 * The campaign page's People tabs — the same split the engine uses when it picks work:
 * up_next = queued and never touched (the queue, in pick order); in_progress = started but not finished
 * (sending, waiting for a follow-back, opener sent, or queued for a later step); done = replied,
 * completed, skipped or failed (an unfollow clean-up may still be due).
 */
export type OutreachStage = "up_next" | "in_progress" | "done";

export interface OutreachStageCounts {
  upNext: number;
  inProgress: number;
  done: number;
}

export type OutreachSkipReason =
  | "already_contacted"
  | "existing_thread"
  | "already_follows_you"
  | "private"
  | "verified"
  | "business"
  | "too_few_followers"
  | "too_many_followers"
  | "missing_keyword"
  | "excluded_keyword"
  | "no_profile_pic"
  | "self"
  | "duplicate"
  | "not_found"
  | "unavailable"
  | "no_follow_back"
  | "no_message"
  | "ai_not_fit"
  | "ai_fit_unavailable"
  | "manual";

export interface OutreachTarget {
  id: ID;
  campaignId: ID;
  igUserId: string | null;
  username: string;
  fullName: string;
  profilePicUrl: string;
  contactId: ID | null;
  status: OutreachTargetStatus;
  phase: OutreachPhase;
  /** Index of the next DM step to send (= DMs already sent). */
  step: number;
  /** Warm-up actions done so far. */
  warmupStep: number;
  nextAt: Millis | null;
  startedAt: Millis | null;
  lastSentAt: Millis | null;
  followedAt: Millis | null;
  /** True only when THIS campaign performed the follow (cleanup only ever unfollows these). */
  followedByCampaign: boolean;
  followBackAt: Millis | null;
  /** Latest confirmed relationship, null when unknown. */
  followsYou?: boolean | null;
  followerCheckedAt?: Millis | null;
  unfollowedAt: Millis | null;
  repliedAt: Millis | null;
  threadId: string | null;
  /** The last message prepared/sent. */
  message: string | null;
  skipReason: OutreachSkipReason | null;
  error: string | null;
  attempts: number;
  createdAt: Millis;
  updatedAt: Millis;
}

// ── Stats ─────────────────────────────────────────────────────────────────

export interface OutreachStats {
  followers?: OutreachFollowerStats;
  started24h?: number;
  total: number;
  queued: number;
  inProgress: number;
  awaitingFollowBack: number;
  /** DMs sent in the last 24h (what dailyCap limits). */
  sent24h: number;
  dailyCap: number;
  /** People who got at least one DM. */
  messaged: number;
  /** Total DMs sent (openers + follow-ups). */
  messagesSent: number;
  replied: number;
  /** People who got a DM or replied (reply-rate denominator). */
  reached: number;
  /** replied / reached, 0–1. */
  replyRate: number;
  followed: number;
  followBacks: number;
  /** followBacks / followed, 0–1. */
  followBackRate: number;
  unfollowed: number;
  /** Contacts from this campaign sitting in a "won" stage, and their deal value (cents). */
  won: number;
  wonValue: number;
  completed: number;
  skipped: number;
  failed: number;
  skippedByReason: Record<string, number>;
  failedByReason: Record<string, number>;
  /** People per campaign-page tab (see OutreachStage). */
  stages: OutreachStageCounts;
}

export interface OutreachCampaignWithStats extends OutreachCampaign {
  stats: OutreachStats;
  /** "Welcome new followers" campaigns only (detail endpoint): which source powers it right now. */
  welcome?: OutreachWelcomeStatus;
}

/** Unique successfully followed or messaged recipients, using latest confirmed evidence. */
export interface OutreachFollowerStats {
  total: number;
  following: number;
  checked: number;
  unknown: number;
  rate: number;
  lastCheckedAt: Millis | null;
}

export interface OutreachOverview {
  followers?: OutreachFollowerStats;
  campaigns: Record<OutreachCampaignStatus, number>;
  sent24h: number;
  messaged: number;
  messagesSent: number;
  replied: number;
  replyRate: number;
  followed: number;
  followBacks: number;
  won: number;
  wonValue: number;
}

// ── Preview ───────────────────────────────────────────────────────────────

export interface OutreachPreviewReq {
  /** Defaults to a built-in sample creator. */
  sampleProfile?: IgUserProfile;
  /** Which step to render (default 0 = opener). */
  step?: number;
  /** Render this variant (default: random). */
  variantIndex?: number;
  /** Override the saved campaign (unsaved edits in the builder). */
  steps?: OutreachStep[];
  ai?: Partial<OutreachAiConfig>;
}

export interface OutreachPreviewRes {
  text: string;
  source: "ai" | "template";
  /** Why the template was used when AI was requested (not configured, failed, cap…). */
  note: string | null;
  template: string;
  profile: IgUserProfile;
}

// ── Engine protocol (extension ↔ API) ─────────────────────────────────────

export interface OutreachNextReq {
  igAccountId: ID;
  /** Action kinds the extension's limiter can't do right now (daily/hourly caps) — skipped server-side. */
  blocked?: ActionKind[];
}

export interface OutreachNextItem {
  targetId: ID;
  campaignId: ID;
  action: OutreachAction;
  /** Limiter kind the write will use. */
  kind: ActionKind;
  igUserId: string | null;
  username: string;
  step: number;
  threadId: string | null;
  /** Read the profile (getProfile(username) when igUserId is null, else getProfileById) and send it to /prepare. */
  needsProfile: boolean;
  /** Read ig.friendship(igUserId) and send it to /prepare (unfollow cleanup). */
  needsFriendship: boolean;
}

export interface OutreachNextRes {
  item: OutreachNextItem | null;
}

export interface OutreachPrepareReq {
  targetId: ID;
  /** Compatibility capability: this client persists/echoes reviewAttemptId in reports. Not auth. */
  reviewedSendProtocol?: 1;
  profile?: IgUserProfile;
  friendship?: IgFriendship;
}

export type OutreachPrepareRes =
  /** Target dropped (filtered, replied, duplicate…). Nothing to do. */
  | { skip: true; action: "skip"; reason: OutreachSkipReason | "replied" }
  /** Nothing to write right now; the target was rescheduled (e.g. you already follow them). */
  | { skip: false; action: "wait"; reason: string; nextAt: Millis | null }
  | {
      skip: false;
      action: "dm";
      kind: "dm_new" | "dm_reply";
      igUserId: string;
      threadId: string | null;
      text: string;
      source: "ai" | "template";
      /** One dispatch attempt for an approved draft. Echo unchanged in /report. */
      reviewAttemptId?: ID;
    }
  | { skip: false; action: "follow" | "like" | "story_view"; kind: ActionKind; igUserId: string }
  /** `cleanup`: which clean-up rule this unfollow is for (older servers leave it out). */
  | { skip: false; action: "unfollow"; kind: ActionKind; igUserId: string; cleanup?: OutreachCleanupReason };

export interface OutreachReportReq {
  targetId: ID;
  reviewAttemptId?: ID;
  ok: boolean;
  action: OutreachAction;
  /** ok + nothing to act on (no stories / no posts to like) — advance without logging a write. */
  noop?: boolean;
  threadId?: string | null;
  itemId?: string;
  mediaId?: string;
  error?: string;
  /** "deferred" = the limiter said not now: back to the queue with no penalty. */
  errorKind?: IgErrorKind | "deferred";
  retryAt?: Millis | null;
}

export interface OutreachReportRes {
  status: OutreachTargetStatus;
  phase: OutreachPhase;
  nextAt: Millis | null;
}

export interface OutreachFollowBackPendingRes {
  items: { targetId: ID; igUserId: string }[];
}

export interface OutreachFollowBackReportReq {
  igAccountId: ID;
  results: { targetId: ID; followedBy: boolean; following?: boolean }[];
  /**
   * Targets Instagram's friendship lookup didn't settle (ig.friendships() omits ids it can't
   * answer for). No verdict: they keep waiting and rotate to the back of the check queue. A wait
   * that runs out while still unsettled is resolved by the server sweep 12 h after the deadline.
   */
  unsettled?: ID[];
}

export interface OutreachFollowBackReportRes {
  checked: number;
  followedBack: number;
  expired: number;
  /** Unsettled targets put back in the queue. */
  deferred: number;
}

export interface OutreachAudienceEstimateRes {
  /** null = unknown until harvested (Instagram sources). */
  count: number | null;
  invalid?: string[];
}

// ── Endpoints (all under /api/outreach; session auth; tenancy enforced) ───
//
// GET    /campaigns?igAccountId=&status=                → { items: OutreachCampaignWithStats[] }
// POST   /campaigns                                     OutreachCampaignInput → { campaign }
// GET    /campaigns/:id                                 → { campaign: OutreachCampaignWithStats }
// PATCH  /campaigns/:id                                 OutreachCampaignPatch → { campaign }   (audience/igAccount/follow.enabled: draft only; usernames may be appended later)
// DELETE /campaigns/:id                                 → { ok }
// POST   /campaigns/:id/status                          { status: "active"|"paused"|"completed" } → { campaign }   (launch validates message, audience, IG account)
// POST   /campaigns/:id/duplicate                       → { campaign }   (a draft copy, no targets)
// GET    /campaigns/:id/stats                           → OutreachStats
// GET    /campaigns/:id/targets?stage=&status=&cursor=&limit=  → Page<OutreachTarget>
//        stage=up_next is ordered as the engine picks (next due first); in_progress by next step due; else latest change first
// PATCH  /campaigns/:id/targets/:targetId               { action: "skip"|"retry" } → { target }
// GET    /campaigns/:id/reviews?cursor=&limit=           → Page<OutreachReview> (pending/approved/in-flight/uncertain current steps)
// POST   /campaigns/:id/reviews/:reviewId/approve        { igUserId, text } → OutreachReview (exact text, 1–1000 chars)
// POST   /campaigns/:id/reviews/:reviewId/skip           → { ok } (ends this person's remaining sequence)
// POST   /campaigns/:id/preview                         OutreachPreviewReq → OutreachPreviewRes
// POST   /preview                                       OutreachPreviewReq & { steps, ai? } → OutreachPreviewRes   (unsaved builder)
// POST   /audience/estimate                             { igAccountId, audience } → OutreachAudienceEstimateRes
// GET    /overview?igAccountId=                         → OutreachOverview
// GET    /welcome-status?igAccountId=                   → OutreachWelcomeStatus   (builder: tracker vs own mode)
// Engine (extension, bearer):
// POST   /next                                          OutreachNextReq → OutreachNextRes   (leases ≤1 target for 10 min)
// POST   /prepare                                       OutreachPrepareReq → OutreachPrepareRes   (filters on first touch, renders the DM)
// POST   /report                                        OutreachReportReq → OutreachReportRes
// GET    /followback/pending?igAccountId=               → OutreachFollowBackPendingRes
// POST   /followback/report                             OutreachFollowBackReportReq → OutreachFollowBackReportRes
// Harvest provider kind "outreach" (core /api/harvest/next|report) feeds Instagram-sourced audiences.

export type OutreachTargetsPage = Page<OutreachTarget>;

export interface OutreachReview {
  id: ID;
  targetId: ID;
  step: number;
  igUserId: string;
  username: string;
  fullName: string;
  threadId: string | null;
  text: string;
  source: "ai" | "template";
  state: "pending" | "approved" | "dispatched" | "sent" | "cancelled" | "uncertain";
  /** Current target hold, e.g. changed recipient or an extension upgrade needed. */
  holdReason?: string | null;
  approvedAt: Millis | null;
  createdAt: Millis;
  updatedAt: Millis;
}

// ── Defaults & limits ─────────────────────────────────────────────────────

export const OUTREACH_LIMITS = {
  maxSteps: 4,
  maxVariants: 10,
  maxVariantLength: 1000,
  /** AI-personalized messages are kept under this. */
  aiMaxChars: 300,
  minDailyCap: 1,
  maxDailyCap: 100,
  defaultDailyCap: 20,
  maxUsernames: 5000,
  maxFollowUpDays: 30,
  maxFollowBackWaitDays: 14,
  maxUnfollowDays: 60,
  maxDelayMinutes: 24 * 60,
  maxKeywords: 30,
} as const;

export const DEFAULT_OUTREACH_FILTERS: OutreachFilters = {
  aiFitCriteria: "",
  skipContacted: true,
  skipExistingThread: true,
  skipFollowers: false,
  skipPrivate: false,
  skipVerified: true,
  skipBusiness: false,
  minFollowers: null,
  maxFollowers: null,
  requireBioKeywords: [],
  excludeBioKeywords: [],
  requireProfilePic: true,
};

export const DEFAULT_FOLLOW_CONFIG: OutreachFollowConfig = {
  enabled: false,
  warmup: { likePosts: 1, viewStories: true },
  messageTiming: "after_follow_back",
  delayMinutes: [10, 60],
  followBackWaitDays: 3,
  noFollowBack: "skip",
  unfollowIfNoReplyDays: null,
  unfollowIfNoFollowBackDays: null,
  unfollowFollowBacksAfterDays: null,
};

/** The builder's starting value when "Unfollow people who follow you back" is switched on. */
export const DEFAULT_UNFOLLOW_FOLLOW_BACKS_DAYS = 3;

export const DEFAULT_OUTREACH_SETTINGS: OutreachCampaignSettings = {
  moveOnSend: true,
  onSendStageId: null,
  onReplyStageId: null,
};

export const DEFAULT_OUTREACH_AI: OutreachAiConfig = { goal: "", offer: "" };

export const DEFAULT_OUTREACH_STEPS: OutreachStep[] = [
  {
    afterDays: 0,
    variants: ["{Hey|Hi|Hey there} [first_name|there]! {Love|Really like} what you're doing with your page. {Quick question|Curious} — what's your main focus right now?"],
    aiPersonalize: false,
  },
  {
    afterDays: 3,
    variants: ["{Hey|Hi} [first_name|there], just bumping this in case it got buried. No pressure at all!"],
    aiPersonalize: false,
  },
];

export const EMPTY_HARVEST_STATE: OutreachHarvestState = {
  cursor: null,
  exhausted: false,
  lastAt: null,
  error: null,
  added: 0,
  seeded: false,
  tracker: null,
};

/** Built-in profile used by the message preview when no sample is given. */
export const SAMPLE_OUTREACH_PROFILE: IgUserProfile = {
  igUserId: "0",
  username: "maya.moves.atx",
  fullName: "Maya Lopez",
  bio: "Pilates instructor in Austin 🌵 Helping new moms get strong again. DM me for class times.",
  category: "Fitness Trainer",
  followers: 2400,
  following: 610,
  posts: 312,
  isPrivate: false,
  isVerified: false,
  isBusiness: true,
};

export const OUTREACH_SKIP_REASON_LABELS: Record<OutreachSkipReason, string> = {
  ai_not_fit: "AI: does not match your criteria",
  ai_fit_unavailable: "AI: insufficient profile evidence",
  already_contacted: "Already contacted",
  existing_thread: "Already in your DMs",
  already_follows_you: "Already follows you",
  private: "Private account",
  verified: "Verified account",
  business: "Business account",
  too_few_followers: "Too few followers",
  too_many_followers: "Too many followers",
  missing_keyword: "Bio missing keywords",
  excluded_keyword: "Bio has an excluded keyword",
  no_profile_pic: "No profile picture",
  self: "That's you",
  duplicate: "In another campaign",
  not_found: "Account not found",
  unavailable: "Can't be messaged",
  no_follow_back: "Didn't follow back",
  no_message: "No message to send",
  manual: "Skipped by you",
};

export const OUTREACH_STATUS_LABELS: Record<OutreachTargetStatus, string> = {
  queued: "Queued",
  sending: "Sending",
  awaiting_follow_back: "Waiting for follow-back",
  sent: "Sent",
  replied: "Replied",
  skipped: "Skipped",
  failed: "Failed",
  completed: "Completed",
};

// ── Helpers shared by the API and the portal (pure) ───────────────────────

/** Expand the saved sequence into executable actions. Legacy campaigns keep their original runner. */
export function campaignPlan(f: OutreachFollowConfig): ("story_view" | "like" | "follow" | "dm")[] {
  return (f.sequence ?? []).flatMap((a): ("story_view" | "like" | "follow" | "dm")[] =>
    a === "follow" ? ["follow"] : a === "message" ? ["dm"] : [
      ...(f.warmup.viewStories ? ["story_view" as const] : []),
      ...Array.from({ length: f.warmup.likePosts }, () => "like" as const),
    ]);
}
export function includesMessage(f: OutreachFollowConfig): boolean { return !f.sequence || f.sequence.includes("message"); }

/** The warm-up actions a Follow + DM campaign does before following, in order. */
export function warmupPlan(follow: Pick<OutreachFollowConfig, "enabled" | "warmup">): ("story_view" | "like")[] {
  if (!follow.enabled) return [];
  const plan: ("story_view" | "like")[] = [];
  if (follow.warmup.viewStories) plan.push("story_view");
  for (let i = 0; i < Math.min(2, Math.max(0, follow.warmup.likePosts)); i++) plan.push("like");
  return plan;
}

function humanMinutes(min: number): string {
  if (min < 1) return `${Math.round(min * 60)} s`;
  if (min < 55) return `${Math.round(min)} min`;
  const h = min / 60;
  return h < 36 ? `${Math.max(1, Math.round(h * 2) / 2)} h` : `${Math.round(h / 24)} days`;
}

/**
 * Plain-English sequence for the builder's live preview, e.g.
 * ["View story", "Like 1 post", "Follow", "Wait ~35 min", "DM opener", "Follow-up in 3 days"].
 */
export function describeSequence(c: { follow: OutreachFollowConfig; steps: Pick<OutreachStep, "afterDays">[] }): string[] {
  const out: string[] = [];
  const f = c.follow;
  if (f.sequence) {
    for (const a of campaignPlan(f)) {
      if (a === "dm" && f.sequence.indexOf("follow") >= 0 && f.sequence.indexOf("follow") < f.sequence.indexOf("message")) {
        out.push(f.messageTiming === "after_follow_back" ? `Wait for follow-back (up to ${f.followBackWaitDays} days)` : f.messageTiming === "after_delay" ? `Wait ${f.delayMinutes[0]}–${f.delayMinutes[1]} min after following` : "Wait 30 s–3 min after following");
      }
      out.push(a === "follow" ? "Follow" : a === "dm" ? "DM opener" : a === "like" ? "Like a recent post" : "View story");
    }
    if (includesMessage(f)) for (const s of c.steps.slice(1)) out.push(`Follow-up in ${s.afterDays} days`);
    if (f.enabled && f.unfollowIfNoFollowBackDays) out.push(`No follow-back → unfollow after ${f.unfollowIfNoFollowBackDays} days`);
    if (f.enabled && f.unfollowIfNoReplyDays) out.push(`No reply → unfollow after ${f.unfollowIfNoReplyDays} days`);
    if (f.enabled && f.unfollowFollowBacksAfterDays) out.push(followBackCleanupLine(f.unfollowFollowBacksAfterDays));
    return out;
  }
  if (f.enabled) {
    if (f.warmup.viewStories) out.push("View story");
    if (f.warmup.likePosts > 0) out.push(`Like ${f.warmup.likePosts} post${f.warmup.likePosts > 1 ? "s" : ""}`);
    out.push("Follow");
    if (f.messageTiming === "after_follow") out.push("Wait 30 s–3 min");
    else if (f.messageTiming === "after_delay") {
      const [a, b] = f.delayMinutes;
      out.push(`Wait ~${humanMinutes((a + b) / 2)}`);
    } else {
      out.push(`Wait for follow-back (up to ${f.followBackWaitDays} day${f.followBackWaitDays === 1 ? "" : "s"})`);
      if (f.noFollowBack === "skip") out.push("No follow-back → skip");
    }
  }
  if (c.steps.length > 0) out.push("DM opener");
  for (let i = 1; i < c.steps.length; i++) {
    const d = c.steps[i]?.afterDays ?? 0;
    out.push(`Follow-up in ${d} day${d === 1 ? "" : "s"}`);
  }
  if (f.enabled && f.unfollowIfNoReplyDays) out.push(`No reply → unfollow after ${f.unfollowIfNoReplyDays} days`);
  if (f.enabled && f.unfollowIfNoFollowBackDays) out.push(`No follow-back → unfollow after the sequence, at least ${f.unfollowIfNoFollowBackDays} day${f.unfollowIfNoFollowBackDays === 1 ? "" : "s"} after following`);
  if (f.enabled && f.unfollowFollowBacksAfterDays) out.push(followBackCleanupLine(f.unfollowFollowBacksAfterDays));
  return out;
}

function followBackCleanupLine(days: number): string {
  return `Follows you back → unfollow ${days} day${days === 1 ? "" : "s"} later`;
}

/** "@Some.User" / "instagram.com/some.user/" → "some.user"; null when it can't be a username. */
export function normalizeUsername(raw: string): string | null {
  let s = (raw ?? "").trim();
  const m = /instagram\.com\/([A-Za-z0-9._]{1,30})/i.exec(s);
  if (m) s = m[1] as string;
  s = s.replace(/^@+/, "").replace(/\/+$/, "").toLowerCase();
  if (!/^[a-z0-9._]{1,30}$/.test(s)) return null;
  if (/^\.|\.$|\.\./.test(s)) return null;
  const reserved = ["direct", "accounts", "explore", "reels", "reel", "p", "stories", "about", "developer", "legal"];
  return reserved.includes(s) ? null : s;
}

/** Split pasted text (newlines, commas, spaces) into unique valid usernames + the rejects. */
export function parseUsernameList(text: string): { usernames: string[]; invalid: string[] } {
  const seen = new Set<string>();
  const usernames: string[] = [];
  const invalid: string[] = [];
  for (const piece of (text ?? "").split(/[\s,;]+/)) {
    if (!piece.trim()) continue;
    const u = normalizeUsername(piece);
    if (!u) {
      if (invalid.length < 50) invalid.push(piece.trim());
      continue;
    }
    if (!seen.has(u)) {
      seen.add(u);
      usernames.push(u);
    }
  }
  return { usernames, invalid };
}
